(ns dashboard.views.questionnaire.components.inference-job-list
  (:require [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [dashboard.common :as common]
            [dashboard.config :as config]
            [dashboard.services.http-config :as http-config]
            [dashboard.notifications :as notifications]
            [dashboard.ui :as ui]
            [dashboard.dev-mode :as dev-mode]
            [dashboard.components.panel :refer [panel]]))

(def -component-id :component/inference-job-list)

(def default-transform-job-duration-reference
  "What's the excpected duration for inference job to run in minutes"
  10)

(def refresh-frequency
  "Transform Job List refresh frequency in seconds"
  30)

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;
;; Events
;;
;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(rf/reg-event-fx
  ::run-inference
  (fn [_ [_ questionnaire-id run-locally? selected-image-tag selected-model-config]]
    {:http-xhrio {:method :post
                  :uri (str "/api/inference-job/start")
                  :format http-config/transit-request-format
                  :response-format http-config/transit-response-format
                  :params {:questionnaire questionnaire-id
                           :run-locally run-locally?
                           :image-tag selected-image-tag
                           :model-config selected-model-config}
                  :timeout http-config/timeout
                  :on-success [::notifications/add {:message "Inference job started"}]
                  :on-failure [::ui/set-error]}}))

(rf/reg-event-fx
  ::fetch-jobs
  (fn [{db :db} [_ q-uuid]]
    {:db       (assoc-in db [-component-id q-uuid :fetching?] true)
     :http-xhrio {:method :get
                  :uri "/api/inference-job/"
                  :params {:questionnaire q-uuid}
                  :format http-config/transit-request-format
                  :response-format http-config/transit-response-format
                  :on-success [::set-jobs q-uuid]
                  :on-failure [::set-failure q-uuid]}}))

(rf/reg-event-db
 ::set-jobs
 (fn [db [_ q-uuid {:keys [can-start-inference jobs]}]]
   (update-in db
              [-component-id q-uuid]
              merge
              {:fetching?            false
               :fetched-timestamp    (js/Date.)
               :can-start-inference? can-start-inference
               ;; sort descending by CreationTime for the view
               :jobs                (reverse (map-indexed (fn [index row]
                                                             (assoc row :index index))
                                                           (sort-by (comp :CreationTime :transform-job)
                                                                    jobs)))
               :error                nil})))

(rf/reg-event-fx
  ::get-start-configuration
  (fn [_ [_ q-uuid]]
    {:http-xhrio {:method :get
                  :uri "/api/inference-job/start"
                  :response-format http-config/transit-response-format
                  :on-success [::set-start-configuration q-uuid]
                  :on-failure [::ui/set-error "Failed to get inference job startup configuration"]}}))

(rf/reg-event-db
 ::set-start-configuration
 (fn [db [_ q-uuid start-configuration]]
   (assoc-in db [-component-id q-uuid :start-configuration] {:options start-configuration
                                                             :selected-image-tag (:default-image-tag start-configuration)
                                                             :selected-model-config (:default-model-config start-configuration)})))

(rf/reg-event-db
  ::set-failure
  (fn [db [_ q-uuid]]
    (update-in db
               [-component-id q-uuid]
               merge
               {:fetching?         false
                :fetched-timestamp (js/Date.)
                :error             "Failed to fetch inference jobs"})))

(rf/reg-event-db
  ::start-polling
  (fn [db [_ questionnaire]]
    (assoc-in db [-component-id questionnaire :polling-timer] (js/setInterval
                                                                (fn []
                                                                  (rf/dispatch [::fetch-jobs questionnaire]))
                                                                (* refresh-frequency 1000)))))

(rf/reg-event-db
  ::stop-polling
  (fn [db [_ questionnaire]]
    (update-in db
               [-component-id questionnaire]
               (fn [{:keys [polling-timer] :as data}]
                 (js/clearInterval polling-timer)
                 (dissoc data :polling-timer)))))

(rf/reg-event-db
  ::set-start-option
  (fn [db [_ questionnaire option-map]]
    (update-in db
               [-component-id questionnaire :start-configuration]
               merge
               option-map)))

(rf/reg-event-db
 ::toggle
 (fn [db [_ questionnaire-id]]
   (let [visible? (get-in db [-component-id questionnaire-id :visible?] false)]
     (assoc-in db [-component-id questionnaire-id :visible?] (not visible?)))))

(rf/reg-event-db
 ::open
 (fn [db [_ questionnaire-id]]
   (assoc-in db [-component-id questionnaire-id :visible?] true)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;
;; Subs
;;
;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(rf/reg-sub
  ::data
  (fn [db [_ q-uuid]]
    (get-in db [-component-id q-uuid])))


(rf/reg-sub
  ::job-ids
  (fn [[_ questionnaire]]
    [(rf/subscribe [::data questionnaire])])
  (fn [[{:keys [jobs]}]]
    (map (fn [job]
           {:index (:index job)
            :id (-> job :transform-job :TransformJobName)})
           jobs)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;
;; The Component
;;
;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn duration-ratio
  "Calculate ratio of duration and avg completed time
  duration in percentages with two decimal accuracy."
  [duration average]
  (-> (/ duration average) ;; ratio
      (.toFixed 2) ;; rounding
      (* 100))) ;; as percentage

(defn start-inference-button [questionnaire-id]
  (let [{:keys [can-start-inference? start-configuration]} @(rf/subscribe [::data questionnaire-id])
        run-locally? (boolean @(rf/subscribe [::dev-mode/get-option [:inference :run-locally?]]))]
    [:button.button.is-small.is-rounded.is-secondary
     {:disabled (not can-start-inference?)
      :on-click (fn [_]
                  (rf/dispatch [::notifications/add {:message "Preparing inference job..."}])
                  (rf/dispatch [::run-inference questionnaire-id (and config/DEV_MODE run-locally?) (:selected-image-tag start-configuration) (:selected-model-config start-configuration)])
                  (rf/dispatch [::open questionnaire-id]))
      :title (str "Find answers to all questionnaire questions.\n\n"
                  "Finds existing answers, based on similarity of question text that the answer was created for.\n\n"
                  "Finds from documents, chunks of text based on similarity of the question text to the text chunk of the document.")}
     "Run Inference"]))

(defn image-selector [questionnaire-id]
  (let [{:keys [start-configuration]} @(rf/subscribe [::data questionnaire-id])
        {{:keys [images default-image-tag]} :options :keys [selected-image-tag]} start-configuration
        run-locally? (boolean @(rf/subscribe [::dev-mode/get-option [:inference :run-locally?]]))]
    [:div {:style {:display "flex"
                   :align-items "center"}}
     [:label {:for "inference-version-selection"}
      [:span "Version: "]]
     [:div.select.is-rounded.ml-2.is-secondary
      {:class [(when-not start-configuration
                 "is-loading")]}
      [:select
       {:id "inference-version-selection"
        :disabled run-locally? ;; We don't support running containers locally, yet
        :value (or selected-image-tag "")
        :on-change (fn [e]
                     (rf/dispatch [::set-start-option questionnaire-id {:selected-image-tag (.. e -target -value)}]))
        :title (str "Select version of the inference application to run.\n\n"
                    "(default) version is recommended.\n\n"
                    "Other versions can contain experimental features, to allow testing with production data.")}
       (map (fn [{:keys [tag]}]
              [:option
               {:key tag
                :value tag}
               (str tag
                    (when (= tag default-image-tag)
                      " (default)"))])
            images)]]]))

(defn model-selector [questionnaire-id]
  (let [{:keys [start-configuration]} @(rf/subscribe [::data questionnaire-id])
        {{:keys [model-configs default-model-config]} :options
         :keys [selected-model-config]} start-configuration]
    [:div {:style {:display "flex"
                   :align-items "center"}}
     [:label {:for "model-version-selection"}
      [:span "Model version: "]]
     [:div.select.is-rounded.ml-2
      {:class [(when-not start-configuration
                 "is-loading")]}
      [:select
       {:id "model-version-selection"
        :value (or selected-model-config "")
        :on-change (fn [e]
                     (rf/dispatch [::set-start-option questionnaire-id {:selected-model-config (.. e -target -value)}]))
        :title (str "Select the version of neural network model to use.\n\n"
                    "(default) version is recommended.\n\n"
                    "Other versions can contain experimental features, to allow testing with production data.")}
       (map (fn [{:keys [model-config]}]
              [:option
               {:key model-config
                :value model-config}
               (str model-config
                    (when (= model-config default-model-config)
                      " (default)"))])
            model-configs)]]]))

(defn run-local-toggle []
  (let [run-locally? (boolean @(rf/subscribe [::dev-mode/get-option [:inference :run-locally?]]))]
    (when config/DEV_MODE
      [:label.ml-4.has-text-weight-normal
       "Run locally?"
       [:input.ml-2
        {:type "checkbox"
         :checked run-locally?
         :on-change (fn [_]
                      (rf/dispatch [::dev-mode/set-option [:inference :run-locally?] (not run-locally?)]))
         :title "Run inference application locally"}]])))

(defn inference-job-list [questionnaire-id]
  (let [{:keys [fetched-timestamp
                fetching?
                visible?
                jobs
                error]} @(rf/subscribe [::data questionnaire-id])
        succesfull-jobs (->> jobs
                             (filter (comp (partial = "Completed")
                                           :TransformJobStatus
                                           :transform-job))
                             (mapv :transform-job))

        avg-duration (if (> (count succesfull-jobs) 0)
                       (/ (apply + (mapv :Duration succesfull-jobs))
                          (count succesfull-jobs))
                       default-transform-job-duration-reference)]
    [panel {:key questionnaire-id
            :class ["is-tertiary mb-1 has-no-shadow"]
            :panel-class :has-background-highlight
            :title [:<>
                    [:span "Inference jobs"]
                    (when fetching? [:div.loader.ml-3])]
            :actions [:<>
                      [start-inference-button questionnaire-id]
                      [:div.ml-4.is-size-7
                       [:div {:style {:display "inline-block"}}
                        [image-selector questionnaire-id]]
                       [:div.ml-4 {:style {:display "inline-block"}}
                        [model-selector questionnaire-id]]]
                      [run-local-toggle]
                      [:button.button.is-small.is-rounded.ml-2.is-secondary
                       {:on-click #(rf/dispatch [::toggle questionnaire-id])
                        :title "View previous runs of inference application"}
                       [:span.icon>i.fas
                        {:class (if visible? "fa-chevron-up" "fa-chevron-down")}]]]}
     (when visible?
       [:<>
        [:div.p-0
         (if (empty? jobs)
           [:span.panel-block.p-2 "No results"]
           [:table.table.is-striped.is-fullwidth
            [:thead.has-text-weight-bold
             [:tr
              [:td "#"]
              [:td "Created"]
              [:td "Finished"]
              [:td "Status"]
              [:td "SageMaker Model"]
              [:td "Details"]]]
            [:tbody
             (for [{{:keys [CreationTime
                            TransformEndTime
                            TransformJobName
                            TransformJobStatus
                            ModelName
                            Duration]} :transform-job
                    :keys [exception index failed_questions]} jobs]

               (let [relative-duration (duration-ratio Duration avg-duration)
                     in-progress? (= "InProgress" TransformJobStatus)]
                 [:tr {:key TransformJobName}
                  [:td index]
                  [:td (if CreationTime
                         (common/format-date-time CreationTime)
                         "-")]
                  [:td (if TransformEndTime
                         (common/format-date-time TransformEndTime)
                         "-")]
                  [:td
                   [:div.is-flex.is-flex-direction-column
                    {:style {:text-align "center"
                             :height "inherit"}}

                    (when-not in-progress?
                      [:span.tag.has-text-weight-bold
                       {:class (cond
                                 (or (= TransformJobStatus "Failed") (seq failed_questions) exception)
                                 "has-text-danger"
                                 (= TransformJobStatus "Stopped") "has-text-warning-dark"
                                 :else "has-text-success")}
                       (cond
                         exception (str "Failed: " (:message exception))

                         (seq failed_questions) (str (count failed_questions) " failed questions")

                         :else TransformJobStatus)])

                    ;; Render progress bar only when status: InProgress
                    (when in-progress?
                      [:progress.progress.is-info.is-large
                       {:style {:border-radius "4px"}
                        ;; show animated progress bar if relative duration
                        ;; is less than five percents
                        :value (when (> relative-duration 5)
                                 relative-duration)
                        :max "100"} relative-duration])]]

                  [:td ModelName]
                  [:td [:a {:href (rfe/href
                                   :route.view/inference-job-detail
                                   {:questionnaire questionnaire-id
                                    :job-name TransformJobName})
                            :title "View inference job details"}
                        "more"]]]))]])]
        [:div.p-0>div.level-right {:style {:width "100%"}}
         (when fetched-timestamp
           [:span.is-size-6.px-2
            "Last sync: " (.toLocaleString fetched-timestamp) (when error [:span.tag.is-danger.mx-3.is-light error])])
         [:button.button.is-info.is-text.mb-2.is-rounded
          {:class    [(when fetching? "is-loading")]
           :on-click (fn [_]
                       (rf/dispatch [::fetch-jobs questionnaire-id]))
           :title (str "Fetch list of inference jobs.\n\n"
                       "The job list is also fetched every " refresh-frequency " seconds.")}
          "Refresh"]]])]))
